import type { MenuBoardContent, MenuItemContent, MenuSection } from './types.js';

/** A section, or the part of one that fits in a column. */
export interface MenuBlock {
  section: MenuSection;
  items: MenuItemContent[];
  /** True when this block carries on a section begun in an earlier column or page. */
  continued: boolean;
}

/** One screenful: blocks laid out in the design's columns. */
export interface MenuPage {
  columns: MenuBlock[][];
}

/**
 * A column a design offers: how much room it has, and what a block costs in it. Units are the
 * design's own (usually `em` at the reference size); only the ratio to `capacity` matters.
 */
export interface MenuColumn {
  capacity: number;
  blockCost: (section: MenuSection, items: MenuItemContent[], continued: boolean) => number;
}

const UNTITLED: MenuSection = {
  id: '',
  title: null,
  titleAr: null,
  subtitle: null,
  subtitleAr: null,
  imageMediaId: null,
};

/**
 * Items grouped by section, in section order and board order within each. Items in no section
 * (or one that no longer exists) join the first; a board with no sections has one untitled one.
 */
export function groupBySection(
  board: Pick<MenuBoardContent, 'items' | 'sections'>,
  exclude?: string | null,
): { section: MenuSection; items: MenuItemContent[] }[] {
  const sections = board.sections?.length ? board.sections : [UNTITLED];
  const known = new Set(sections.map((s) => s.id));
  const groups = sections.map((section) => ({ section, items: [] as MenuItemContent[] }));
  for (const item of board.items) {
    if (exclude && item.id === exclude) continue;
    const index = item.sectionId && known.has(item.sectionId)
      ? sections.findIndex((s) => s.id === item.sectionId)
      : 0;
    groups[index]!.items.push(item);
  }
  return groups;
}

/**
 * Flows sections into columns, splitting a section that does not fit and starting a new page
 * when the columns run out. A block always takes at least one item, so an item too big for a
 * column still appears rather than looping forever. Always returns at least one page.
 */
/** A group to pack; `continued` when it carries on from an earlier page. */
type Group = { section: MenuSection; items: MenuItemContent[]; continued?: boolean };

export function packColumns(
  groups: Group[],
  columns: MenuColumn[],
  options: { balance?: boolean } = {},
): MenuPage[] {
  const pages = packOnce(groups, columns);
  if (!options.balance || columns.length < 2) return pages;
  // Spread what is on the last page (the only page, for a short menu) across every column
  // instead of piling it into the first ones.
  const last = pages[pages.length - 1]!;
  // Halves of a section split across columns become one group again, so it can be kept whole.
  const leftovers: Group[] = [];
  for (const block of last.columns.flat()) {
    const previous = leftovers[leftovers.length - 1];
    if (block.continued && previous?.section.id === block.section.id) {
      previous.items = [...previous.items, ...block.items];
    } else {
      leftovers.push({ section: block.section, items: [...block.items], continued: block.continued });
    }
  }
  const spread = balanceOnePage(leftovers, columns);
  if (spread) pages[pages.length - 1] = spread;
  return pages;
}

/**
 * Shrinks the columns until the groups only just fit on one page: the smallest scale that
 * still fits is the most even spread. A layout that keeps every section whole wins over a more
 * even one that splits a section under a repeated heading. Null when nothing fits.
 */
function balanceOnePage(groups: Group[], columns: MenuColumn[]): MenuPage | null {
  let even: MenuPage | null = null;
  for (let step = 1; step < 20; step += 1) {
    const scale = step / 20;
    const tighter = columns.map((c) => ({ ...c, capacity: c.capacity * scale }));
    const attempt = packOnce(groups, tighter);
    if (attempt.length !== 1) continue;
    const page = attempt[0]!;
    even ??= page;
    const splits = page.columns.flat().filter((b) => b.continued).length;
    if (splits === groups.filter((g) => g.continued).length) return page;
  }
  return even;
}

function packOnce(groups: Group[], columns: MenuColumn[]): MenuPage[] {
  const pages: MenuPage[] = [];
  let page: MenuPage = { columns: columns.map(() => []) };
  let column = 0;
  let used = 0;

  const nextColumn = () => {
    column += 1;
    used = 0;
    if (column >= columns.length) {
      pages.push(page);
      page = { columns: columns.map(() => []) };
      column = 0;
    }
  };

  for (const group of groups) {
    if (group.items.length === 0) continue;
    let block: MenuBlock = { section: group.section, items: [], continued: group.continued ?? false };
    // A section that will not fit in what is left of this column, but would fit whole in a
    // fresh one, starts there rather than being split under a repeated heading.
    const start = columns[column]!;
    const whole = (spec: MenuColumn) => spec.blockCost(group.section, group.items, block.continued);
    if (used > 0 && used + whole(start) > start.capacity) {
      const next = columns[(column + 1) % columns.length]!;
      if (whole(next) <= next.capacity) nextColumn();
    }
    for (const item of group.items) {
      const spec = columns[column]!;
      const candidate = [...block.items, item];
      const fits = used + spec.blockCost(block.section, candidate, block.continued) <= spec.capacity;
      if (!fits && (block.items.length > 0 || used > 0)) {
        if (block.items.length > 0) {
          page.columns[column]!.push(block);
          block = { section: group.section, items: [], continued: true };
        }
        nextColumn();
      }
      block.items.push(item);
    }
    const spec = columns[column]!;
    page.columns[column]!.push(block);
    used += spec.blockCost(block.section, block.items, block.continued);
  }

  if (page.columns.some((c) => c.length > 0) || pages.length === 0) pages.push(page);
  return pages;
}
